import { computed, inject, Injectable, signal } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { DevicesDataService } from '../../../../services/data/devices-data/devices-data.service';
import { TAB_VALUE } from '../../../../shared/components/listing-page/listing-page.types';
import {
  chartHealthSegmentsColors,
  createDeviceBadgesFn,
  deviceTypeTableIcon,
  SENSORS_DEFAULT_SEARCH_TYPE,
  SENSORS_SEARCH_TYPES
} from '../../../../shared/components/listing-page/services/listing-page-model/listing-page-model.constants';
import {
  ListingDataSource,
  ListingPageModel
} from '../../../../shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import {
  batteryStatusTooltip,
  deviceHealthTooltip,
  lastHeardTooltip,
  validReadRateTooltip
} from '../../../../shared/components/listing-table/listing-page.consts';
import { ListingTableComponent } from '../../../../shared/components/listing-table/listing-table.component';
import { LocationColumnComponent } from '../../../../shared/components/table-columns/location-column.component';
import { DeviceSidePanelTitleComponent } from '../components/device-side-panel-title/device-side-panel-title.component';
import { DeviceSidePanelComponent } from '../components/device-side-panel/device-side-panel.component';
import { devicesListingSource, devicesTableSortField } from '../constants/devices.constants';
import { DeviceListingFieldName, DevicesPayload, OrderDirection, TableDevices } from '../components/devices.models';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { StatusDotColumnComponent } from 'src/app/shared/components/listing-table/components/status-dot-column.component';
import { BubbleColumnComponent } from 'src/app/shared/components/listing-table/components/bubble-column.component';
import { ExportCsvConfig, ExportDataService } from 'src/app/services/file/export-data.service';
import { MeterTable } from 'src/app/services/data/devices-data/devices-data.types';
import { ListingPageModelAbstract } from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model-abstract';
import { ListingPageContext } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-context.types';
import { DevicesMapComponent } from '@portal/shared/components/map-report-group/map-report-group.component';
import { Applicable } from 'src/app/pages/administration/report-groups/report-groups.types';
import { ListingPageId } from '@portal/shared/components/listing-page/store/listing-page-store.types';
import { ListingPageModelRegistry } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-model-registry.service';
import { SensorsListingFiltersSourceService } from './filter-sources/sensors-listing-filters-source.service';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import {
  CustomizableColumnComponent,
  CustomizableColumnType,
  CustomizableTableColumnValue
} from '@portal/shared/components/listing-table/components/customizable-column.component';
import { SerialNumberColumnComponent } from '../components/column/serial-number-column.component';
import { CommDeviceIdColumnComponent } from '../components/column/comm-device-id-column.component';

@Injectable()
export class SensorsListingModelService extends ListingPageModelAbstract implements ListingPageModel {
  private readonly devices = inject(DevicesDataService);
  private readonly exportService = inject(ExportDataService);
  readonly listingPageModelRegistry = inject(ListingPageModelRegistry);
  readonly timezoneService = inject(TimezoneService);

  readonly pageTitle = TranslateRegistry.get.SENSORS.TITLE.SENSORS;
  readonly searchTypeList = computed(() => SENSORS_SEARCH_TYPES);
  readonly selectedSearchType = computed(() => SENSORS_DEFAULT_SEARCH_TYPE);
  readonly sorting = computed(() => ({
    columnName: DeviceListingFieldName.LastHeard,
    orderDirection: OrderDirection.DESC
  }));
  readonly tableColumns = signal([
    {
      field: 'commDeviceId',
      title: TranslateRegistry.get.SENSORS.VIEW_TABLE.COMM_DEVICE_ID,
      width: 200,
      sortable: false,
      cmp: CommDeviceIdColumnComponent
    },
    {
      field: 'serialNumber',
      title: TranslateRegistry.get.SENSORS.VIEW_TABLE.SERIAL_NUMBER,
      width: 200,
      sortable: false,
      cmp: SerialNumberColumnComponent
    },
    {
      field: 'deviceTypeName',
      title: TranslateRegistry.get.SENSORS.VIEW_TABLE.DEVICE_TYPE,
      sortable: true,
      width: 250,
      icon: deviceTypeTableIcon
    },
    {
      field: 'gnnId',
      title: TranslateRegistry.get.SENSORS.VIEW_TABLE.GNN_ID,
      sortable: false,
      width: 250,
      cmp: CustomizableColumnComponent,
      options: {
        columnType: CustomizableColumnType.TEXT,
        columnValues: (tableItem: MeterTable): CustomizableTableColumnValue[] =>
          tableItem.servicePoints.map(servicePoint => {
            return {
              text: servicePoint.serviceLocationId ?? ''
            };
          })
      }
    },
    {
      field: 'status',
      title: TranslateRegistry.get.SENSORS.VIEW_TABLE.STATUS,
      width: 250,
      sortable: false,
      cmp: StatusDotColumnComponent,
      textColor: '#070C0F'
    },
    {
      field: 'deviceStatusValue',
      title: TranslateRegistry.get.SENSORS.VIEW_TABLE.HEALTH,
      width: 300,
      sortable: true,
      cmp: BubbleColumnComponent,
      tooltip: deviceHealthTooltip
    },
    {
      field: 'lastHeard',
      title: TranslateRegistry.get.SENSORS.VIEW_TABLE.LAST_HEARD,
      width: 300,
      sortable: true,
      tooltip: lastHeardTooltip
    },
    {
      field: 'batteryStatus',
      title: TranslateRegistry.get.SENSORS.VIEW_TABLE.BATTERY_STATUS,
      width: 200,
      sortable: false,
      cmp: BubbleColumnComponent,
      tooltip: batteryStatusTooltip
    },
    {
      field: 'validTransmissionReadRateStatus',
      title: TranslateRegistry.get.SENSORS.VIEW_TABLE.VALID_READ_RATE_STATUS,
      width: 200,
      sortable: false,
      cmp: BubbleColumnComponent,
      tooltip: validReadRateTooltip
    },
    {
      field: 'location',
      title: TranslateRegistry.get.SENSORS.VIEW_TABLE.LOCATION,
      width: 250,
      sortable: false,
      cmp: LocationColumnComponent
    }
  ]);
  readonly dataSource: ListingDataSource<TableDevices> = {
    getAll: tableState => {
      return this.devices.prepareDataForTable(tableState);
    },
    total: 0
  };
  readonly exportCsv = {
    dataSource: {
      export: (payload: Pick<DevicesPayload, 'filters' | 'sort'>): Observable<ExportCsvConfig[]> => {
        return this.devices.export(payload).pipe(
          map(response =>
            response.map(
              sensor =>
                ({
                  commDeviceId: {
                    title: 'CommDeviceID',
                    value: sensor.commDeviceId ?? sensor.parentCommDeviceId ?? ''
                  },
                  serialNumber: {
                    title: 'SerialNumber',
                    value: sensor.serialNumber ?? ''
                  },
                  deviceTypeName: { title: 'DeviceType', value: sensor.deviceTypeName },
                  gnnId: {
                    title: 'GNNID',
                    value: sensor.servicePoints.reduce((acc, item, idx) => {
                      const serviceLocationId = item.serviceLocationId ?? '';
                      return idx > 0 ? `${acc}\n${serviceLocationId}` : serviceLocationId;
                    }, '')
                  },
                  status: { title: 'Status', value: sensor.status },
                  deviceStatusValue: { title: 'DeviceHealth', value: sensor.deviceStatusValue },
                  lastHeard: {
                    title: 'LastHeard',
                    value: sensor.lastHeard ? this.timezoneService.formatLong(sensor.lastHeard) : ''
                  },
                  batteryStatus: { title: 'BatteryStatus', value: sensor.batteryStatus },
                  validTransmissionReadRateStatus: {
                    title: 'ValidReadRateStatus',
                    value: sensor.validTransmissionReadRateStatus
                  },
                  location: [
                    { title: 'Latitude', value: sensor.geoMetaData?.latitude ?? '' },
                    { title: 'Longitude', value: sensor.geoMetaData?.longitude ?? '' },
                    { title: 'Comment', value: sensor.geoMetaData?.comments ?? '' }
                  ]
                }) as ExportCsvConfig
            )
          ),
          tap(data => this.exportService.exportDeviceRecordToCsv(data, 'Export-Sensors', this.pageId))
        );
      }
    }
  };
  readonly selectionFieldName = 'deviceUID';
  readonly chartSegmentsColors = chartHealthSegmentsColors;
  readonly createBadgesFn = createDeviceBadgesFn;

  readonly mapCmp = DevicesMapComponent;
  readonly tableCmp = ListingTableComponent;
  readonly contentClasses: string[] = ['listing-page__content_breakpoint'];
  readonly defaultTab = TAB_VALUE.Table;
  readonly sortField = devicesTableSortField;
  readonly sidePanelBodyCmp = DeviceSidePanelComponent;
  readonly sidePanelTitleCmp = DeviceSidePanelTitleComponent;
  readonly createMapCmpInputs = (ctx: ListingPageContext): Record<string, unknown> => {
    return {
      selectedLocationData: ctx.selectedLocation(),
      filtersPayload: ctx.mapFilters(),
      chartSegmentsColors: this.chartSegmentsColors,
      createBadgesFn: this.createBadgesFn,
      locationSelectedFn: ctx.onLocationSelected,
      applicable: Applicable.AmiHealthDashboard,
      actionSource: devicesListingSource,
      pageId: this.pageId
    };
  };

  readonly pageId = ListingPageId.Sensors;
  readonly filterSource = inject(SensorsListingFiltersSourceService);
  constructor() {
    super();
    const listingPageModelRegistry = this.listingPageModelRegistry;

    listingPageModelRegistry.add(this.pageId, this);
  }
}
